// @wc-ignore-file
/**
 * CR-237: after a GitHub OAuth round-trip the admin lands back on the project
 * Integrations page with the GitHub card already in edit mode, so the owner,
 * repository and base branch can be picked straight away. CR-274: Linear does
 * the same, and with no mapping saved yet its new-mapping form opens directly.
 * CR-295: Jira behaves exactly like Linear.
 *
 * The authorize step records the service in sessionStorage under
 * OAUTH_RETURN_EDIT_SERVICE_KEY; the callback page reads it (one-shot) and adds
 * `?edit=<service>` to the project Integrations route; the page opens the
 * editor once the connection and its saved config are loaded, then drops the
 * param.
 *
 * The marker is an explicit opt-in recorded where the flow starts (the
 * Add-integration dialog, the only OAuth entry point since CR-169), rather than
 * derived from oauth_service in the callback: a future entry point that should
 * not reopen the editor (e.g. a reconnect) simply does not set it.
 *
 * CR-288: this module also parses the route's other search param, `?connect=`.
 */
import { isKnownService } from "./integration-services";
import type { Service } from "./integrations.types";

export const OAUTH_RETURN_EDIT_SERVICE_KEY = "oauth_return_edit_service";

/** The services whose card reopens in edit mode after OAuth. */
export type ReturnEditService = "github" | "linear" | "jira";

/** Narrows an untrusted value (search param, sessionStorage) to a ReturnEditService. */
export function parseReturnEditService(value: unknown): ReturnEditService | undefined {
  return value === "github" || value === "linear" || value === "jira" ? value : undefined;
}

/**
 * CR-288 D4: `?connect=<service>` (set by the /connect/$service deep link)
 * opens the Add-integration dialog on that service's Authorize step.
 */
export function parseConnectService(value: unknown): Service | undefined {
  return typeof value === "string" && isKnownService(value) ? value : undefined;
}

/** The project Integrations route's search params. */
export type IntegrationsSearch = { edit?: ReturnEditService; connect?: Service };

/** validateSearch for the project Integrations route: unknown values are dropped. */
export function validateIntegrationsSearch(search: Record<string, unknown>): IntegrationsSearch {
  return {
    edit: parseReturnEditService(search.edit),
    connect: parseConnectService(search.connect),
  };
}
